import { useId, useState, type CSSProperties } from 'react';
import { cn } from '@/lib/cn';
import { CoinMark } from './CoinMark';
import { formatPercent, formatPrice, instruments, useQuotes } from './markets';
import './demo-panel.css';

/** Para tutari, fiyat degil: her zaman iki hane. */
const money = (value: number) =>
  `$${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const amounts = [10, 25, 50, 100] as const;
const leverages = [1, 250, 500, 1000] as const;

/** The house's cut in the reference: 1% of the stake, not of the leveraged total. */
const feeRate = 0.01;

/**
 * A demo of Moon's betting panel.
 *
 * Nothing here places anything: it is a calculator over a live price, so someone
 * can see what a stake and a multiplier actually mean before they open an
 * account. The numbers are the real formulas — a promo panel that flatters the
 * maths would be worse than none, because the one figure that matters is how
 * quickly the position dies.
 */
export function DemoBetPanel({ clickUrl, accent }: { clickUrl: string; accent: string }) {
  const { data: quotes, isLoading, isError, isPaused } = useQuotes();
  const directionNoteId = useId();

  const [symbol, setSymbol] = useState<string>(instruments[0].symbol);
  const [amount, setAmount] = useState<number>(25);
  const [leverage, setLeverage] = useState<number>(100);
  const [side, setSide] = useState<'up' | 'down' | null>(null);

  const instrument = instruments.find((i) => i.symbol === symbol) ?? instruments[0];
  const quote = quotes?.find((q) => q.symbol === symbol);
  const price = quote?.price;

  const position = amount * leverage;
  const fee = amount * feeRate;

  /*
   * Liquidation: the price at which the stake is gone.
   *
   *   long  -> entry × (1 − 1/leverage)
   *   short -> entry × (1 + 1/leverage)
   *
   * The simple form, ignoring maintenance margin and funding, which is what the
   * platform's own panel shows. At 1000× that is a tenth of a percent — the
   * number this panel exists to make visible.
   */
  const liquidation =
    price === undefined
      ? undefined
      : side === 'down'
        ? price * (1 + 1 / leverage)
        : price * (1 - 1 / leverage);

  /*
   * Kac yuzde ters hareket pozisyonu bitirir. Turkce bicimde: toFixed nokta
   * verir ve sayfanin geri kalani virgul kullanir.
   */
  const moveToLiquidation = (100 / leverage).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <section className="moon-demo" style={{ '--demo-accent': accent } as CSSProperties}>
      <div className="moon-demo-heading">
        <div>
          <p className="moon-demo-eyebrow">Senaryonu oluştur</p>
          <h2>Demo bahis paneli</h2>
        </div>
        <span className="moon-demo-badge">Simülasyon</span>
      </div>
      <p className="moon-demo-intro">
        Bir piyasa seç, tutar ve kaldıracın hesaplamayı nasıl değiştirdiğini gör.
      </p>

      <label className="moon-demo-instrument">
        <span className="sr-only">Enstrüman</span>
        <CoinMark code={instrument.code} className="moon-demo-coin" />
        <div className="moon-demo-instrument-choice">
          <span aria-hidden="true">Seçili piyasa</span>
          <select
            aria-label="Enstrüman"
            value={symbol}
            onChange={(event) => setSymbol(event.target.value)}
          >
            {instruments.map((option) => (
              <option key={option.symbol} value={option.symbol}>
                {option.code} — {option.name}
              </option>
            ))}
          </select>
        </div>
        <span className="moon-demo-quote">
          <span className="moon-demo-price">
            {price === undefined ? (isLoading ? '···' : '—') : formatPrice(price)}
          </span>
          {quote ? (
            <span
              className={cn('moon-demo-change', quote.changePercent >= 0 ? 'is-up' : 'is-down')}
            >
              {formatPercent(quote.changePercent)}
              <span className="moon-demo-period"> / 24 sa.</span>
            </span>
          ) : (
            <span className="moon-demo-quote-loading">
              {isPaused ? 'Çevrimdışı' : isError ? 'Fiyata ulaşılamıyor' : 'Fiyat bekleniyor'}
            </span>
          )}
        </span>
      </label>

      <div className="moon-demo-controls">
        <div className="moon-demo-control">
          <Field label="Bahis tutarı" step="01">
            <div className="moon-demo-input">
              <span aria-hidden="true">$</span>
              <input
                type="number"
                min={1}
                step={1}
                value={amount}
                onChange={(event) => setAmount(Math.max(1, Number(event.target.value) || 0))}
              />
              <span className="moon-demo-input-unit" aria-hidden="true">
                USD
              </span>
            </div>
          </Field>
          <Choices
            options={amounts.map((value) => ({ value, label: `$${value}` }))}
            selected={amount}
            onSelect={setAmount}
            label="Hazır tutarlar"
          />
        </div>
        <div className="moon-demo-control">
          <Field label="Kaldıraç" step="02">
            <div className="moon-demo-input">
              <input
                type="number"
                min={1}
                max={1000}
                step={1}
                value={leverage}
                onChange={(event) =>
                  setLeverage(Math.min(1000, Math.max(1, Number(event.target.value) || 1)))
                }
              />
              <span aria-hidden="true">×</span>
            </div>
          </Field>
          <Choices
            options={leverages.map((value) => ({ value, label: `${value}x` }))}
            selected={leverage}
            onSelect={setLeverage}
            label="Hazır kaldıraçlar"
          />
        </div>
      </div>

      <div className="moon-demo-result">
        <div className="moon-demo-result-heading">
          <span>Hesap özeti</span>
          <span>{side === 'down' ? 'Aşağı yön' : 'Yukarı yön'}</span>
        </div>
        <dl>
          <div className="moon-demo-position">
            <dt>Pozisyon büyüklüğü</dt>
            <dd>{money(position)}</dd>
          </div>
          <Line
            label={side === 'down' ? 'Tasfiye fiyatı (aşağı)' : 'Tasfiye fiyatı (yukarı)'}
            value={liquidation === undefined ? '—' : formatPrice(liquidation)}
            tone="warn"
          />
          <Line label="Tahmini ücret" value={money(fee)} />
        </dl>
        <p className="moon-demo-risk">
          {`Bu simülasyonda ücret, bahis tutarının %1'i üzerinden hesaplanır. Fiyat %${moveToLiquidation} ters yönde hareket ederse pozisyon tasfiye olur ve bahis tutarının tamamı gider.`}
        </p>
      </div>

      {/* Native links keep new-tab, middle-click and mobile long-press behaviour. */}
      <div className="moon-demo-directions">
        <a
          href={clickUrl}
          target="_blank"
          rel="noreferrer noopener sponsored"
          aria-describedby={directionNoteId}
          onClick={() => setSide('up')}
          className={cn('moon-demo-direction moon-demo-up', side === 'up' && 'is-selected')}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <path d="m5 16 7-8 7 8M12 8v12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Yukarı
          <ExternalArrow />
        </a>
        <a
          href={clickUrl}
          target="_blank"
          rel="noreferrer noopener sponsored"
          aria-describedby={directionNoteId}
          onClick={() => setSide('down')}
          className={cn('moon-demo-direction moon-demo-down', side === 'down' && 'is-selected')}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <path d="m5 8 7 8 7-8M12 16V4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Aşağı
          <ExternalArrow />
        </a>
      </div>
      <p id={directionNoteId} className="moon-demo-disclaimer">
        Burada işlem yapılmaz. Yön bağlantıları Moon’u yeni sekmede açar.
      </p>
    </section>
  );
}

function Field({
  label,
  step,
  children,
}: {
  label: string;
  step: string;
  children: React.ReactNode;
}) {
  return (
    <label className="moon-demo-field">
      <span className="moon-demo-field-label">
        <span aria-hidden="true">{step}</span>
        {label}
      </span>
      {children}
    </label>
  );
}

function Choices<T extends number>({
  options,
  selected,
  onSelect,
  label,
}: {
  options: { value: T; label: string }[];
  selected: number;
  onSelect: (value: T) => void;
  label: string;
}) {
  return (
    <div className="moon-demo-presets" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === selected}
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Line({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div className="moon-demo-result-line">
      <dt>{label}</dt>
      <dd className={cn(tone === 'warn' && 'moon-demo-result-warn')}>{value}</dd>
    </div>
  );
}

function ExternalArrow() {
  return (
    <svg
      className="moon-demo-external"
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    >
      <path d="M7 17 17 7M7 7h10v10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
